import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Copy, Check, Image as ImageIcon } from "lucide-react";

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/** Build absolute URL to serve image (works for both dev + production) */
function mediaUrl(id) {
  return `${API_BASE}/media/${id}`;
}

export default function MediaManager() {
  const [items, setItems] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const fileInputRef = useRef();

  const load = async () => {
    setItems(null);
    try {
      const { data } = await api.get("/admin/media");
      setItems(data);
    } catch {
      setItems([]);
      toast.error("Eroare la încărcarea galeriei");
    }
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const file of files) {
      try {
        const form = new FormData();
        form.append("file", file);
        await api.post("/admin/media", form, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch (err) {
        fail++;
        toast.error(`${file.name}: ${formatApiErrorDetail(err.response?.data?.detail) || err.message}`);
      }
    }
    if (ok > 0) toast.success(`${ok} imagine(i) încărcate`);
    setUploading(false);
    fileInputRef.current.value = "";
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Ștergi această imagine?")) return;
    await api.delete(`/admin/media/${id}`);
    toast.success("Șters");
    load();
  };

  const copyUrl = async (item) => {
    const url = mediaUrl(item.id);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(item.id);
      setTimeout(() => setCopiedId(null), 2000);
      toast.success("URL copiat");
    } catch {
      toast.error("Nu s-a putut copia URL-ul");
    }
  };

  return (
    <div data-testid="media-manager">
      <div className="border border-black p-6 mb-8" data-testid="upload-area">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono-label text-black/60">Încarcă imagini</div>
            <div className="font-display font-medium uppercase text-2xl mt-1">Media Library</div>
            <div className="text-sm text-black/60 mt-1">JPG, PNG, WebP · Maxim 5MB per fișier</div>
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" data-testid="file-input" />
          <button onClick={() => fileInputRef.current.click()} disabled={uploading} data-testid="upload-btn"
            className="font-mono-label bg-black text-white px-6 py-3 border border-black hover:bg-white hover:text-black disabled:opacity-50 inline-flex items-center gap-2">
            <Upload size={14} /> {uploading ? "Se încarcă..." : "Încarcă imagini"}
          </button>
        </div>
      </div>

      {items === null ? (
        <div className="font-mono-label text-black/60">Se încarcă...</div>
      ) : items.length === 0 ? (
        <div className="border border-black p-12 text-center">
          <ImageIcon size={48} className="mx-auto opacity-30" />
          <div className="font-mono-label text-black/60 mt-4">Nu ai încărcat încă nicio imagine</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="media-grid">
          {items.map((item) => (
            <div key={item.id} className="border border-black flex flex-col" data-testid={`media-item-${item.id}`}>
              <div className="aspect-[4/3] overflow-hidden border-b border-black">
                <img src={mediaUrl(item.id)} alt={item.filename} className="w-full h-full object-cover" />
              </div>
              <div className="p-3 flex-1 flex flex-col gap-2">
                <div className="text-sm font-mono truncate" title={item.filename}>{item.filename}</div>
                <div className="text-xs text-black/60 font-mono">{formatSize(item.size)}</div>
                <div className="flex gap-2 mt-auto">
                  <button onClick={() => copyUrl(item)} data-testid={`copy-url-${item.id}`}
                    className="flex-1 font-mono-label border border-black px-2 py-2 hover:bg-black hover:text-white inline-flex items-center justify-center gap-1 text-[10px]">
                    {copiedId === item.id ? <><Check size={12} /> Copiat</> : <><Copy size={12} /> URL</>}
                  </button>
                  <button onClick={() => del(item.id)} data-testid={`delete-media-${item.id}`}
                    className="font-mono-label border border-black px-2 py-2 hover:bg-red-600 hover:text-white hover:border-red-600">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
